import {
  groupsAdapter,
  profilesAdapter,
  rolesAdapter,
  sitesAdapter,
  usersAdapter,
} from '@redux/authent/authent.adapters';
import { authentSchema, groupSchema, userSchema } from '@redux/authent/authent.schema';
import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { normalizeOne } from '@utils/schema.utils';
import { WritableDraft } from 'immer';
import { normalize } from 'normalizr';

import { GroupLight, GroupWithUsers, NormalizedGroup, Profile } from '@/types/authent/groups.types';
import { AuthentSettings, AuthSource, LogoutCauseEnum, NormalizedUser, User } from '@/types/authent/login.types';
import { Role, RoleEnum } from '@/types/authent/roles.types';
import { Site } from '@/types/authent/sites.types';

const emptyUser = {
  id: -1,
  name: '',
  firstName: '',
  email: '',
  login: '',
  password: '',
  active: false,
  deactivationDate: null,
  sites: [],
  groups: [],
  activeGroup: null,
  activeSite: null,
  createdBy: null,
  safetyKey: null,
  authSource: AuthSource.JDBC,
  creationTime: null,
  modifiedBy: null,
  modificationTime: null,
};

export type AuthentState = {
  activeUser: User;
  token: string | null;
  renewalToken: string | null;
  sessionUuid: string | null;
  roles: EntityState<Role, string>;
  profiles: EntityState<Profile, number>;
  sites: EntityState<Site, string>;
  users: EntityState<NormalizedUser, number>;
  groups: EntityState<NormalizedGroup, string>;
  authentSettings: AuthentSettings | null;
  hasBeenInitialized: boolean;
  hasSelectedGroup: boolean;
  isChangingShift: boolean;
  forcedLogoutCause: LogoutCauseEnum;
};

export const AUTHENT_INITIAL_STATE: AuthentState = {
  activeUser: emptyUser,
  token: null,
  renewalToken: null,
  sessionUuid: null,
  roles: rolesAdapter.getInitialState(),
  profiles: profilesAdapter.getInitialState(),
  sites: sitesAdapter.getInitialState(),
  users: usersAdapter.getInitialState(),
  groups: groupsAdapter.getInitialState(),
  authentSettings: null,
  hasBeenInitialized: false,
  hasSelectedGroup: false,
  isChangingShift: false,
  forcedLogoutCause: LogoutCauseEnum.NONE,
};

const updateGroupOnActiveUser = (state: WritableDraft<AuthentState>, group: GroupWithUsers) => {
  if (state.activeUser.activeGroup?.code === group.code) {
    state.activeUser.activeGroup = group;
  }
  state.activeUser.groups = state.activeUser.groups.map((g) => {
    return group.code === g.code ? ({ id: group.id, code: group.code, name: group.name } as GroupLight) : g;
  });
};

const authent = createSlice({
  name: 'authent',
  initialState: AUTHENT_INITIAL_STATE,
  reducers: {
    userLogout(state, action: PayloadAction<Partial<AuthentState> | undefined>) {
      return { ...AUTHENT_INITIAL_STATE, ...action.payload, forcedLogoutCause: state.forcedLogoutCause };
    },
    updateToken(state, action: PayloadAction<string | null>) {
      state.token = action.payload;
    },
    updateRenewalToken(state, action: PayloadAction<string | null>) {
      state.renewalToken = action.payload;
    },
    updateSessionUuid(state, action: PayloadAction<string | null>) {
      state.sessionUuid = action.payload;
    },
    updateActiveUser(state, action: PayloadAction<User>) {
      state.activeUser = action.payload;
    },
    updateHasBeenInitialized(state, action: PayloadAction<boolean>) {
      state.hasBeenInitialized = action.payload;
    },
    updateHasSelectedGroup(state, action: PayloadAction<boolean>) {
      state.hasSelectedGroup = action.payload;
    },
    updateIsChangingShift(state, action: PayloadAction<boolean>) {
      state.isChangingShift = action.payload;
    },
    updateForcedLogoutCause: (state, action: PayloadAction<LogoutCauseEnum>) => {
      state.forcedLogoutCause = action.payload;
    },
    updateAuthent: (
      state,
      action: PayloadAction<{
        profiles: Profile[];
        users: User[];
        roles: Role[];
        sites: Site[];
        authentSettings: AuthentSettings | null;
        groups: GroupWithUsers[];
        activeUser: User;
        forcedLogoutCause: LogoutCauseEnum;
      }>,
    ) => {
      const { roles, profiles, sites, authentSettings, activeUser, forcedLogoutCause } = action.payload;
      const { users, groups } = normalize(action.payload, authentSchema).entities as unknown as {
        users?: NormalizedUser[];
        groups?: NormalizedGroup[];
      };

      rolesAdapter.setAll(state.roles, roles);
      profilesAdapter.setAll(state.profiles, profiles);
      sitesAdapter.setAll(state.sites, sites);
      usersAdapter.setAll(state.users, users ?? []);
      groupsAdapter.setAll(state.groups, groups ?? []);
      state.authentSettings = authentSettings;
      state.activeUser = activeUser;
      state.forcedLogoutCause = forcedLogoutCause;
    },
    addProfile: ({ profiles }, action: PayloadAction<Profile>) => {
      profilesAdapter.addOne(profiles, action.payload);
    },
    updateProfile: (state, action: PayloadAction<Profile>) => {
      const canAccessProfiles = state.activeUser.activeGroup?.profile?.roles.includes(RoleEnum.ACCESS_PROFILE);
      canAccessProfiles && profilesAdapter.upsertOne(state.profiles, action.payload);
      if (state.activeUser.activeGroup?.profile?.name === action.payload.name) {
        state.activeUser.activeGroup.profile = action.payload;
      }
    },
    deleteProfile: ({ profiles }, action: PayloadAction<Profile>) => {
      profilesAdapter.removeOne(profiles, action.payload.id);
    },
    addSite: ({ sites }, action: PayloadAction<Site>) => {
      sitesAdapter.addOne(sites, action.payload);
    },
    updateSite: ({ sites }, action: PayloadAction<Site>) => {
      sitesAdapter.upsertOne(sites, action.payload);
    },
    deleteSite: ({ sites }, action: PayloadAction<Site>) => {
      sitesAdapter.removeOne(sites, action.payload.code);
    },
    addUser: ({ users }, action: PayloadAction<User>) => {
      const normalizedUser = normalizeOne(action.payload, userSchema);
      normalizedUser && usersAdapter.addOne(users, normalizedUser as NormalizedUser);
    },
    updateUser: (state, action: PayloadAction<User>) => {
      const normalizedUser = normalizeOne(action.payload, userSchema);
      const canAccessUsers = state.activeUser.activeGroup?.profile?.roles.includes(RoleEnum.ACCESS_USER);
      canAccessUsers && normalizedUser && usersAdapter.upsertOne(state.users, normalizedUser as NormalizedUser);

      if (state.activeUser.login === action.payload.login) {
        state.activeUser = action.payload;
      }
    },
    updateUsers: (state, action: PayloadAction<User[]>) => {
      const normalizedUsers: NormalizedUser[] = [];
      const canAccessUsers = state.activeUser.activeGroup?.profile?.roles.includes(RoleEnum.ACCESS_USER);

      action.payload.forEach((user) => {
        if (canAccessUsers) {
          const normalizedUser = normalizeOne(user, userSchema);
          normalizedUser && normalizedUsers.push(normalizedUser as NormalizedUser);
        }
        if (state.activeUser.login === user.login) {
          state.activeUser = user;
        }
      });
      usersAdapter.upsertMany(state.users, normalizedUsers);
    },
    deleteUser: ({ users }, action: PayloadAction<User>) => {
      usersAdapter.removeOne(users, action.payload.id);
    },
    addGroup: ({ groups }, action: PayloadAction<GroupWithUsers>) => {
      const normalizedGroup = normalizeOne(action.payload, groupSchema);
      normalizedGroup && groupsAdapter.addOne(groups, normalizedGroup as NormalizedGroup);
    },
    updateGroup: (state, action: PayloadAction<GroupWithUsers>) => {
      const normalizedGroup = normalizeOne(action.payload, groupSchema);
      const canAccessGroups = state.activeUser.activeGroup?.profile?.roles.includes(RoleEnum.ACCESS_GROUP);
      canAccessGroups && normalizedGroup && groupsAdapter.upsertOne(state.groups, normalizedGroup as NormalizedGroup);
      updateGroupOnActiveUser(state, action.payload);
    },
    updateGroups: (state, action: PayloadAction<GroupWithUsers[]>) => {
      const normalizedGroups: NormalizedGroup[] = [];
      const canAccessGroups = state.activeUser.activeGroup?.profile?.roles.includes(RoleEnum.ACCESS_GROUP);
      action.payload.forEach((group) => {
        if (canAccessGroups) {
          const normalizedGroup = normalizeOne(group, groupSchema);
          normalizedGroup && normalizedGroups.push(normalizedGroup as NormalizedGroup);
        }
        updateGroupOnActiveUser(state, group);
      });
      groupsAdapter.upsertMany(state.groups, normalizedGroups);
    },
    deleteGroup: ({ groups }, action: PayloadAction<GroupWithUsers>) => {
      groupsAdapter.removeOne(groups, action.payload.code);
    },
    updateAuthentSettings(state, action: PayloadAction<AuthentSettings>) {
      state.authentSettings = action.payload;
    },
  },
});

export const {
  userLogout,
  updateHasBeenInitialized,
  updateForcedLogoutCause,
  updateActiveUser,
  updateToken,
  updateRenewalToken,
  updateSessionUuid,
  updateAuthent,
  addProfile,
  updateProfile,
  deleteProfile,
  addSite,
  updateSite,
  deleteSite,
  addUser,
  updateUser,
  updateUsers,
  deleteUser,
  addGroup,
  updateGroup,
  updateGroups,
  deleteGroup,
  updateAuthentSettings,
  updateHasSelectedGroup,
  updateIsChangingShift,
} = authent.actions;

export default authent.reducer;
